import { QuizQuestion, Quiz } from '../store/types';

/**
 * Generate a random quiz from a question bank
 * @param questions Array of questions for the course
 * @param courseId Course ID
 * @param numQuestions Number of questions to include (default: 10)
 * @param allowedDifficulties Optional array of allowed difficulty levels (e.g., ['easy', 'medium'])
 * @returns Quiz object with randomly selected questions
 */
export const generateQuiz = (
  questions: QuizQuestion[],
  courseId: string,
  numQuestions: number = 10,
  allowedDifficulties?: ('easy' | 'medium' | 'hard')[]
): Quiz => {
  // Filter questions by allowed difficulties if specified
  let filteredQuestions = questions;
  if (allowedDifficulties && allowedDifficulties.length > 0) {
    filteredQuestions = questions.filter((q) => {
      const difficulty = q.difficulty || 'medium';
      return allowedDifficulties.includes(difficulty);
    });
  }
  
  // If no questions match the filter, fall back to all questions
  if (filteredQuestions.length === 0) {
    filteredQuestions = questions;
  }
  
  // Shuffle questions array using Fisher-Yates algorithm
  const shuffled = [...filteredQuestions].sort(() => Math.random() - 0.5);
  
  // Select first numQuestions (or all if less available)
  const selectedQuestions = shuffled.slice(0, Math.min(numQuestions, filteredQuestions.length));
  
  // Generate quiz ID
  const quizId = `quiz_${courseId}_${Date.now()}`;
  
  return {
    id: quizId,
    courseId,
    questions: selectedQuestions,
    timestamp: new Date().toISOString(),
  };
};

/**
 * Calculate quiz result from answers
 */
export const calculateQuizResult = (
  quiz: Quiz,
  answers: Record<string, number>,
  _userId: string | null // Prefixed with _ to indicate intentionally unused
): {
  score: number;
  totalQuestions: number;
  correctCount: number;
  questions: Array<{
    questionId: string;
    questionText: string;
    userAnswerIndex: number;
    userAnswer: string;
    correctAnswerIndex: number;
    correctAnswer: string;
    isCorrect: boolean;
  }>;
} => {
  let correctCount = 0;
  const questionResults = quiz.questions.map((question) => {
    const userAnswerIndex = answers[question.id] ?? -1;
    const isCorrect = userAnswerIndex === question.correctAnswerIndex;
    if (isCorrect) correctCount++;
    
    return {
      questionId: question.id,
      questionText: question.questionText,
      userAnswerIndex,
      userAnswer: userAnswerIndex >= 0 ? question.options[userAnswerIndex] : 'Not answered',
      correctAnswerIndex: question.correctAnswerIndex,
      correctAnswer: question.options[question.correctAnswerIndex],
      isCorrect,
    };
  });

  const totalQuestions = quiz.questions.length;
  const score = totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;

  return {
    score: Math.round(score),
    totalQuestions,
    correctCount,
    questions: questionResults,
  };
};

